import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircle2, Plus, Trash2, ArrowLeft, Sparkles, Loader2, Download, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandLogo from '../../components/BrandLogo';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import EmailGate, { useEmailGate } from '../../components/EmailGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyExp = () => ({ company: '', role: '', period: '', highlights: '' });

export default function ResumeBuilderPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('Jane Doe');
  const [role, setRole] = useState('Senior Product Designer');
  const [email, setEmail] = useState('jane@example.com');
  const [phone, setPhone] = useState('+1 415 555 0101');
  const [location, setLocation] = useState('San Francisco, CA');
  const [links, setLinks] = useState('linkedin.com/in/jane · dribbble.com/jane');
  const [summaryHint, setSummaryHint] = useState('Design leader with 8 years shaping consumer + SaaS products used by 20M+ people.');
  const [exp, setExp] = useState([
    { company: 'Airbnb', role: 'Senior Product Designer', period: '2022 - Present', highlights: 'Led host tools redesign, launched Airbnb Experiences 2.0' },
    { company: 'Stripe', role: 'Product Designer', period: '2019 - 2022', highlights: 'Owned checkout, Wallet integrations, Apple Pay expansion' },
  ]);
  const [skills, setSkills] = useState('Product Design, Figma, Design Systems, UX Research, Prototyping, Motion');
  const [education, setEducation] = useState('BFA Graphic Design — Rhode Island School of Design, 2016');
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState(null);
  const pdfRef = useRef(null);
  const gate = useEmailGate('resume');

  const addExp = () => setExp([...exp, emptyExp()]);
  const removeExp = (i) => setExp(exp.filter((_, idx) => idx !== i));
  const updateExp = (i, k, v) => { const n = [...exp]; n[i] = { ...n[i], [k]: v }; setExp(n); };

  const generate = async () => {
    if (!name.trim() || !role.trim()) return toast({ title: 'Name & target role required' });
    setLoading(true);
    setResume(null);
    try {
      const r = await fetch(`${API}/templates/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, target_role: role, email, phone, location,
          summary_hint: summaryHint || undefined,
          experience: exp,
          skills: skills || undefined,
          education: education || undefined,
          links: links || undefined,
        }),
      });
      if (!r.ok) throw new Error(`Server error (${r.status})`);
      const data = await r.json();
      setResume(data);
    } catch (e) {
      toast({ title: 'Generation failed', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    gate.run(async () => {
      if (!pdfRef.current) return;
      toast({ title: 'Preparing PDF…' });
      const canvas = await html2canvas(pdfRef.current, { scale: 2, backgroundColor: '#ffffff' });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      const pageH = pdf.internal.pageSize.getHeight();
      let heightLeft = h, position = 0;
      pdf.addImage(img, 'PNG', 0, position, w, h, undefined, 'FAST');
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position = heightLeft - h;
        pdf.addPage();
        pdf.addImage(img, 'PNG', 0, position, w, h, undefined, 'FAST');
        heightLeft -= pageH;
      }
      pdf.save(`${(resume?.name || 'resume').replace(/\s+/g, '_')}.pdf`);
      toast({ title: 'Resume downloaded ✅' });
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0512] text-white">
      <div className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <button data-testid="resume-back" onClick={() => navigate('/')} className="text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft size={16} /> Home
          </button>
          <span className="text-white/25">/</span>
          <span className="text-sm text-white/80">Templates</span>
          <span className="text-white/25">/</span>
          <span className="text-sm text-fuchsia-300 font-semibold">Resume Builder</span>
          <div className="ml-auto"><BrandLogo size={24} tone="light" /></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[420px_1fr] gap-8">
        <div className="space-y-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 mb-4">
              <UserCircle2 size={13} className="text-fuchsia-300" />
              <span className="text-[11px] tracking-widest uppercase font-bold text-white/85">Resume Builder</span>
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Craft a resume in 60 seconds</h1>
            <p className="text-sm text-white/60 mt-1.5">Give us the rough details. AI polishes it. You download a PDF.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input data-testid="resume-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
            <input data-testid="resume-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Target role" className="h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className="h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
            <input value={links} onChange={(e) => setLinks(e.target.value)} placeholder="Links (comma-sep)" className="h-10 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
          </div>

          <textarea value={summaryHint} onChange={(e) => setSummaryHint(e.target.value)} placeholder="Career summary (1-2 lines, AI will polish)" rows={2} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Experience</label>
              <button data-testid="resume-add-exp" onClick={addExp} className="text-fuchsia-300 text-xs font-bold flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
            {exp.map((e, i) => (
              <div key={i} data-testid={`resume-exp-${i}`} className="p-3 rounded-xl bg-white/4 border border-white/10 mb-2 space-y-2">
                <div className="flex items-center justify-between text-[10px] tracking-widest uppercase text-white/50 font-bold">
                  Role {i + 1}
                  {exp.length > 1 && <button onClick={() => removeExp(i)} className="text-rose-300 hover:text-rose-200"><Trash2 size={12} /></button>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={e.company} onChange={(v) => updateExp(i, 'company', v.target.value)} placeholder="Company" className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-fuchsia-400 outline-none text-sm" />
                  <input value={e.period} onChange={(v) => updateExp(i, 'period', v.target.value)} placeholder="Period" className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-fuchsia-400 outline-none text-sm" />
                </div>
                <input value={e.role} onChange={(v) => updateExp(i, 'role', v.target.value)} placeholder="Role" className="w-full h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-fuchsia-400 outline-none text-sm" />
                <textarea value={e.highlights} onChange={(v) => updateExp(i, 'highlights', v.target.value)} placeholder="Highlights (a sentence or two — AI will craft bullets)" rows={2} className="w-full px-2.5 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-fuchsia-400 outline-none text-sm" />
              </div>
            ))}
          </div>

          <textarea value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="Skills (comma-separated)" rows={2} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />
          <textarea value={education} onChange={(e) => setEducation(e.target.value)} placeholder="Education" rows={2} className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-fuchsia-400 outline-none text-sm" />

          <div className="space-y-2">
            <Button data-testid="resume-generate" onClick={generate} disabled={loading} className="w-full h-12 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold">
              {loading ? <><Loader2 className="mr-2 animate-spin" size={16} /> Crafting…</> : <><Sparkles className="mr-2" size={16} /> Generate resume</>}
            </Button>
            {resume && (
              <Button data-testid="resume-download" onClick={download} className="w-full h-11 rounded-xl bg-white text-black font-bold">
                <Download size={16} className="mr-2" /> Download PDF
              </Button>
            )}
          </div>
        </div>

        <div>
          {!resume && !loading && (
            <div className="border-2 border-dashed border-white/10 rounded-2xl p-16 text-center text-white/50 min-h-[500px] flex flex-col items-center justify-center">
              <UserCircle2 size={40} className="text-white/25 mb-4" />
              <div className="font-bold text-white/80">Your polished resume appears here</div>
              <div className="text-sm mt-2">Fill in the form and hit Generate.</div>
            </div>
          )}
          {loading && (
            <div className="border-2 border-dashed border-fuchsia-400/30 rounded-2xl p-16 text-center min-h-[500px] flex flex-col items-center justify-center">
              <Loader2 size={40} className="text-fuchsia-300 mb-4 animate-spin" />
              <div className="font-bold">Polishing your resume…</div>
              <div className="text-sm text-white/60 mt-2">Rewriting bullets with metrics via Claude 4.5</div>
            </div>
          )}
          {resume && <ResumeDoc data={resume} innerRef={pdfRef} />}
        </div>
      </div>
      <Toaster />
      <EmailGate {...gate} />
    </div>
  );
}

function ResumeDoc({ data, innerRef }) {
  return (
    <div ref={innerRef} className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#ffffff', color: '#0f172a' }}>
      {/* Header */}
      <div className="p-10 pb-6" style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #f5f3ff 100%)' }}>
        <div className="font-display text-4xl md:text-5xl font-black tracking-[-0.03em] text-slate-900">{data.name}</div>
        <div className="mt-1 text-fuchsia-600 font-bold text-lg">{data.role}</div>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600 font-mono">
          {data.email && <span className="flex items-center gap-1"><Mail size={11} /> {data.email}</span>}
          {data.phone && <span className="flex items-center gap-1"><Phone size={11} /> {data.phone}</span>}
          {data.location && <span className="flex items-center gap-1"><MapPin size={11} /> {data.location}</span>}
          {(data.links || []).map((l, i) => (
            <a key={i} href={l.url} className="text-fuchsia-600 hover:underline">{l.label || l.url}</a>
          ))}
        </div>
      </div>

      {/* Summary */}
      {data.summary && (
        <div className="px-10 pb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Summary</div>
          <p className="text-slate-700 leading-relaxed text-[15px]">{data.summary}</p>
        </div>
      )}

      {/* Experience */}
      {data.experience?.length > 0 && (
        <div className="px-10 pb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Experience</div>
          <div className="space-y-5">
            {data.experience.map((e, i) => (
              <div key={i}>
                <div className="flex items-baseline justify-between flex-wrap gap-x-3">
                  <div className="font-bold text-slate-900 text-[15px]">{e.role} <span className="text-slate-400 font-normal">·</span> <span className="text-fuchsia-600">{e.company}</span></div>
                  <div className="text-xs text-slate-500 font-mono">{e.period}{e.location ? ` · ${e.location}` : ''}</div>
                </div>
                {e.bullets?.length > 0 && (
                  <ul className="mt-1.5 space-y-1">
                    {e.bullets.map((b, j) => (
                      <li key={j} className="flex gap-2 text-slate-700 text-sm leading-relaxed">
                        <span className="text-fuchsia-500 mt-1">▸</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills + Education */}
      <div className="px-10 pb-6 grid md:grid-cols-2 gap-8">
        {data.skills?.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Skills</div>
            <div className="flex flex-wrap gap-1.5">
              {data.skills.map((s, i) => (
                <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 font-medium">{s}</span>
              ))}
            </div>
          </div>
        )}
        {data.education?.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Education</div>
            <div className="space-y-2">
              {data.education.map((e, i) => (
                <div key={i}>
                  <div className="font-bold text-slate-900 text-sm">{e.degree}</div>
                  <div className="text-xs text-slate-600">{e.institution} · <span className="font-mono">{e.period}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-10 pb-8 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-400 font-bold">
        <span>Crafted with Around the World AI</span>
        <span>aroundtheworld.ai</span>
      </div>
    </div>
  );
}
